import { useGameStore } from '@/store/useGameStore';
import type { MatchState } from '@/common/model';
import { track } from '@/lib/analytics';
import { isSetOver } from '@/utils/matchSetsToWin';
import { currentMatchPendingCount, useSyncStore } from '../useSyncStore';

export const SYNC_MAX_RETRIES = 5;
export const SYNC_RETRY_DELAYS = [1000, 2000, 5000, 10000, 30000];
export const SYNC_BATCH_SIZE = 10;
export const SYNC_BATCH_DELAY_MS = 100;
export const SYNC_REQUEST_TIMEOUT_MS = 20_000;
/**
 * How long an action may hold back its match on server errors (5xx) before the
 * tablet checks whether the server records the next one (VOL-208): long enough
 * for a restart or a deploy, short enough for the second device and the review.
 */
export const SYNC_SET_ASIDE_AFTER_MS = 3 * 60_000;
/** Gap between the two last tries of a stuck action, long enough for a server restart. */
export const SYNC_STUCK_RETRY_GAP_MS = 5_000;

let lastTrackedMismatch: string | null = null;

/** Test-only reset of the mismatch already reported. */
export function __resetTrackedMismatch(): void {
  lastTrackedMismatch = null;
}

export function reconcileScoresFromServer(serverState: MatchState): void {
  const store = useSyncStore.getState();
  if (currentMatchPendingCount(store) > 0) return;

  const game = useGameStore.getState();
  // The rally's last action is already on the server while its point waits for
  // « Valider le point » on the tablet: count it on the tablet side too.
  const pendingPoint = game.gamePhase === 'PointEnd' ? game.pointWinner : null;
  const localUs = game.scoreUs + (pendingPoint === 'us' ? 1 : 0);
  const localThem = game.scoreThem + (pendingPoint === 'them' ? 1 : 0);
  // The tablet opens each set itself (DOMAIN_RULES B5). A server state on another
  // set means the replay disagrees on a set end: applying it moved the tablet
  // back a set mid-play and filed every following action there (VOL-214), or
  // jumped it to the next set while the set-end screen was still open.
  if (serverState.currentSet !== game.currentSet) {
    // A server one set ahead while the tablet shows a finished set is the normal
    // set-end window. Ahead on an unfinished set, the replay closed it too early
    // (VOL-221); behind, it missed the set end (VOL-214).
    const serverAhead = serverState.currentSet > game.currentSet;
    const localSetOver = isSetOver(localUs, localThem, game.currentSet, game.setsToWinMatch);
    const mismatchKey = `${serverState.matchId}:${game.currentSet}:${serverState.currentSet}`;
    // Sent once per mismatch: every sync batch replays the same state until it is resolved.
    if ((!serverAhead || !localSetOver) && mismatchKey !== lastTrackedMismatch) {
      lastTrackedMismatch = mismatchKey;
      track('live_resync_set_mismatch', {
        match_id: serverState.matchId,
        local_set: game.currentSet,
        server_set: serverState.currentSet,
        direction: serverAhead ? 'server_ahead' : 'server_behind',
      });
    }
    return;
  }
  // The tablet's score is the truth (B5): the server's is only taken when it is
  // ahead on both sides, e.g. points the tablet lost on a reload. A lower server
  // score means the replay disagrees (a set it closed too early, then reopened),
  // and copying it produced impossible set scores (VOL-221). While a point waits
  // for validation, confirming it would count it a second time.
  if (pendingPoint) return;
  if (serverState.scoreUs < localUs || serverState.scoreThem < localThem) {
    // Not copied any more, so measured: the replay scores this set differently.
    const scoreKey = `${serverState.matchId}:${game.currentSet}:score`;
    if (scoreKey !== lastTrackedMismatch) {
      lastTrackedMismatch = scoreKey;
      track('live_resync_score_behind', {
        match_id: serverState.matchId,
        set: game.currentSet,
        local_score: `${localUs}-${localThem}`,
        server_score: `${serverState.scoreUs}-${serverState.scoreThem}`,
      });
    }
    return;
  }
  if (game.scoreUs !== serverState.scoreUs || game.scoreThem !== serverState.scoreThem) {
    useGameStore.getState().loadMatchState({
      scoreUs: serverState.scoreUs,
      scoreThem: serverState.scoreThem,
      currentSet: serverState.currentSet,
      setScores: serverState.setScores,
    });
  }
}
